import React, { useEffect, useRef } from 'react';
import { Overview } from '../review';

const LANE_CLASSES = ['pause', 'vt', 'possible_vf', 'afib', 'bradycardia', 'tachycardia'];
const LANE_COLOUR: Record<string, string> = {
  pause: '#f59e0b',
  vt: '#f43f5e',
  possible_vf: '#e11d48',
  afib: '#a855f7',
  bradycardia: '#38bdf8',
  tachycardia: '#34d399',
};

interface OverviewLanesProps {
  overview: Overview;
  /** Seconds currently in the strip viewer, drawn as a cursor. */
  viewStart: number;
  viewEnd: number;
  onSeek: (seconds: number) => void;
}

/**
 * The whole study on one screen (§7.2): HR trend, one lane per finding class, and the quality lane.
 * Built for a 60-second orientation — where the AFib lives, when the pauses happened, what is noise.
 */
export const OverviewLanes: React.FC<OverviewLanesProps> = ({ overview, viewStart, viewEnd, onSeek }) => {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const classes = LANE_CLASSES.filter((c) => overview.episodes.some((e) => e.rhythm_class === c)).concat(
    [...new Set(overview.episodes.map((e) => e.rhythm_class))].filter((c) => !LANE_CLASSES.includes(c)),
  );
  const HR_H = 90;
  const LANE_H = 14;
  const height = HR_H + 18 + (classes.length + 1) * (LANE_H + 4) + 14;

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const width = rect.width;
    const LABEL_W = 86;
    const plotW = width - LABEL_W;
    const duration = overview.duration_seconds || 1;
    const xOf = (s: number) => LABEL_W + (s / duration) * plotW;

    ctx.fillStyle = '#090d16';
    ctx.fillRect(0, 0, width, rect.height);
    ctx.font = '10px JetBrains Mono, monospace';

    // Day boundaries.
    for (let d = 86400; d < duration; d += 86400) {
      ctx.strokeStyle = 'rgba(148,163,184,0.25)';
      ctx.beginPath();
      ctx.moveTo(xOf(d), 0);
      ctx.lineTo(xOf(d), rect.height);
      ctx.stroke();
    }

    // HR trend, 30–180 bpm.
    const yOf = (bpm: number) => 8 + (1 - (Math.min(180, Math.max(30, bpm)) - 30) / 150) * (HR_H - 8);
    ctx.fillStyle = '#94a3b8';
    ctx.fillText('HR bpm', 4, 14);
    for (const ref of [60, 100, 150]) {
      ctx.strokeStyle = 'rgba(148,163,184,0.12)';
      ctx.beginPath();
      ctx.moveTo(LABEL_W, yOf(ref));
      ctx.lineTo(width, yOf(ref));
      ctx.stroke();
      ctx.fillText(String(ref), LABEL_W - 24, yOf(ref) + 3);
    }
    const { bin_seconds, bpm } = overview.hr_trend;
    ctx.strokeStyle = '#22d3ee';
    ctx.lineWidth = 1;
    ctx.beginPath();
    let pen = false;
    bpm.forEach((v, i) => {
      if (v === null) {
        pen = false;
        return;
      }
      const x = xOf((i + 0.5) * bin_seconds);
      if (!pen) ctx.moveTo(x, yOf(v));
      else ctx.lineTo(x, yOf(v));
      pen = true;
    });
    ctx.stroke();

    let y = HR_H + 18;
    classes.forEach((cls) => {
      ctx.fillStyle = '#94a3b8';
      ctx.fillText(cls, 4, y + 10);
      ctx.fillStyle = 'rgba(30,41,59,0.6)';
      ctx.fillRect(LABEL_W, y, plotW, LANE_H);
      for (const e of overview.episodes.filter((ep) => ep.rhythm_class === cls)) {
        ctx.fillStyle = LANE_COLOUR[cls] ?? '#cbd5e1';
        ctx.globalAlpha = e.status === 'UNREVIEWED' ? 0.55 : 1;
        ctx.fillRect(xOf(e.onset_seconds), y, Math.max(2, xOf(e.offset_seconds) - xOf(e.onset_seconds)), LANE_H);
        ctx.globalAlpha = 1;
      }
      y += LANE_H + 4;
    });

    ctx.fillStyle = '#94a3b8';
    ctx.fillText('quality', 4, y + 10);
    ctx.fillStyle = 'rgba(16,185,129,0.25)';
    ctx.fillRect(LABEL_W, y, plotW, LANE_H);
    // Two different things, drawn differently (§6.11). Grey is signal with no countable beats at
    // all — a detached electrode. Amber hatching is signal whose beats are fine but which is too
    // noisy to call a rhythm from: findings were withheld there, not absent. Collapsing the two
    // would tell a reviewer either that a good recording is half lost, or that a stretch nothing
    // was judged in came back clean.
    ctx.fillStyle = 'rgba(245,158,11,0.55)';
    for (const [s, e] of overview.noisy_spans ?? []) {
      ctx.fillRect(xOf(s), y, Math.max(1, xOf(e) - xOf(s)), LANE_H);
    }
    ctx.fillStyle = 'rgba(100,116,139,0.9)';
    for (const [s, e] of overview.unanalyzable_spans) {
      ctx.fillRect(xOf(s), y, Math.max(1, xOf(e) - xOf(s)), LANE_H);
    }

    // Where the strip viewer currently is.
    ctx.strokeStyle = '#f8fafc';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(xOf(viewStart), 2, Math.max(2, xOf(viewEnd) - xOf(viewStart)), rect.height - 4);
  }, [overview, viewStart, viewEnd, classes.join('|')]);

  // No panel chrome of its own: rendered inside the same bordered panel as WaveformViewer
  // (ReviewWorkspace) so the two read as one merged view — the chart's map, not a separate
  // section you have to remember relates to it (§ layout, 2026-09-14).
  return (
    <div>
      <div className="mb-2 flex items-center justify-between px-1 text-xs">
        <span className="font-bold uppercase tracking-wide text-white">Study overview</span>
        <span className="text-slate-500">Click to jump · in the quality lane, grey is unanalyzable signal and amber is too noisy to judge a rhythm · faded bars are unreviewed</span>
      </div>
      <canvas
        ref={ref}
        style={{ height }}
        className="block w-full cursor-pointer rounded-lg"
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - r.left - 86;
          if (x < 0) return;
          onSeek((x / (r.width - 86)) * overview.duration_seconds);
        }}
      />
    </div>
  );
};
